import Swal from 'sweetalert2'
import { DateTime } from 'luxon'
import { deepDelete } from '@/helpers/deepDelete'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { QUERY_UPSERT_OMNI_CAMPAIGN, QUERY_UPSERT_DRAFT_OMNI_CAMPAIGN } from '@/modules/maria/campanha-da-maria/graphql'
import { getChannelsPermissionsStatus } from '@/modules/campanhas/gestao-ofertas-v2/helpers/channels'

export const mariaCampaign = {
  data: () => ({
    step: 1,
    forcePush: false,
    loadingSaveNext: false,
    loadingSaveDraft: false,
    tryingToSaveAsDraft: false,
  }),

  computed: {
    ...mapGetters(['campaign', 'searchedProducts', 'importedProducts', 'searchProductsValue', 'loadingAction', 'campaignVariablesConfiguration']),
    formattedWeekDays() {
      return {
        domingo: 'SUNDAY',
        'segunda-feira': 'MONDAY',
        'terça-feira': 'TUESDAY',
        'quarta-feira': 'WEDNESDAY',
        'quinta-feira': 'THURSDAY',
        'sexta-feira': 'FRIDAY',
        sábado: 'SATURDAY',
      }
    },
    isMariaCampaignFinished() {
      return this.campaign.status === 'FINISHED'
    },
    isMariaCampaignActive() {
      return this.campaign.status === 'ACTIVE'
    },
    isMariaCampaignDraft() {
      return this.campaign.status === 'DRAFT'
    },
    isMariaCampaignPersonalized() {
      return !!this.campaign.personalized
    },
    isCampaignActivated() {
      return ['ACTIVE', 'SCHEDULED'].includes(this.campaign.status)
    },
    isCreatingNewCampaign() {
      return !this.campaign._id
    },
    itsGetCampaignFromApi() {
      return this.$route.params.id
    },
    hasSegments() {
      return this.campaign.segments && this.campaign.segments.length
    },
    campaignHasOffers() {
      return this.campaign.offer_schedules && this.campaign.offer_schedules.length > 0
    },
    offerWithSegment() {
      if (!Array.isArray(this.campaign.offer_schedules)) {
        return false
      }

      return this.campaign.offer_schedules.some((schedule) => {
        const offerSchedule = schedule.offer_schedule
        if (offerSchedule && Array.isArray(offerSchedule?.segments) && offerSchedule.segments.length > 0) {
          return true
        }

        if (Array.isArray(schedule?.segments) && schedule.segments.length > 0) {
          return true
        }

        return false
      })
    },
    selectedSegments() {
      return this.campaign.segments
    },
    getChannelsPermissions() {
      return getChannelsPermissionsStatus(this.$permissions, this.$getAllowedModules)
    },
    hasOfferSalesPermission() {
      return this.$permissions.havePagePermission('campanhas/gestao_ofertas/ofertas') && this.$getAllowedModules()['offer_management']
    },
    hasFranchiseeOfferSelected() {
      const franchiseeOffersSelected = this.campaign?.offer_schedules?.filter((offer) => offer.from_franchise) || []
      return franchiseeOffersSelected.length > 0
    },
    itsGetCampaignFromOffers() {
      return this.$route.params.offerScheduleId
    },
    campaignVariableConfigurationId() {
      return (
        this.campaign?.messages_dispatcher?.settings?.campaign_variable_configurations?.[0]?._id ??
        this.campaign?.messages_dispatcher?.settings?.campaign_variable_configurations[0]
      )
    },
    unitsFromOfferSchedules() {
      if (!this.campaignHasOffers) return []
      return [
        ...new Set(
          this.campaign.offer_schedules
            .map((offerSchedule) => {
              return offerSchedule?.units?.map((unit) => unit.id_erp || unit)
            })
            .flat() || []
        ),
      ]
    },
  },
  methods: {
    ...mapMutations(['RESET_MARIA_CAMPAIGN_MODULE', 'SET_OMNI_CAMPAIGN', 'SET_SEARCHED_PRODUCTS', 'SET_IMPORTED_PRODUCTS', 'SET_SEARCH_PRODUCTS_VALUE']),
    ...mapActions([
      'setOmniCampaignKey',
      'setProductsSearchValue',
      'setLoadingAction',
      'getOmniCampaign',
      'upsertCampaignVariablesConfiguration',
      'getCampaignVariablesConfiguration',
    ]),
    goToErrorField() {
      setTimeout(() => {
        const errorElement = document.querySelector('.error--text')

        if (errorElement && errorElement.offsetParent !== null) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      }, 100)
    },
    formattedBaseOfferSales(offer) {
      return {
        active: this.isUndefined(offer.active) ? true : offer.active,
        maximum_per_client: this.isUndefined(offer.maximum_per_client) ? 0 : offer.maximum_per_client,
        minimum_per_client: this.isUndefined(offer.minimum_per_client) ? 0 : offer.minimum_per_client,
        price_1: this.isUndefined(offer.price_1) ? 0 : offer.price_1,
        price_2: this.isUndefined(offer.price_2) ? 0 : offer.price_2,
        image_url: offer.image_url || offer?.product?.image || offer?.image,
        measure_name: offer?.measure_name || offer?.product?.measure_name,
      }
    },
    hasOfferGroupAssociation(offer) {
      return Boolean(offer?.isGroup || offer?.groupInfo?._id || offer?.group_id || offer?.group_syntactic_id)
    },
    getOfferGroupReferenceFields(offer) {
      if (!offer) {
        return {}
      }

      const identifier = offer.group_syntactic_id || offer.groupInfo?._id || offer.group_id || offer._id

      if (!identifier) {
        return {}
      }

      const isSyntactic = Boolean(offer.group_syntactic || offer.group_syntactic_id)

      return isSyntactic ? { group_syntactic_id: identifier, group_syntactic: true } : { group_id: identifier }
    },
    formattedBaseOfferGroup(offer) {
      const groupReference = this.getOfferGroupReferenceFields(offer)

      return {
        group_name: offer.nameGroup || offer.group_name || offer.name,
        ...groupReference,
        group_rules_exhibition: offer.group_rules_exhibition,
        group_image_url: offer.group_image_url || offer.groupInfo?.image_url || offer.image_url || offer?.image,
      }
    },
    formatOfferSales(data) {
      let offerSales = []
      data.forEach((offer) => {
        if (['CampaignOfferSalesGroup', 'OfferSalesGroup'].includes(offer.__typename) || offer.isGroup) {
          if (offer.offer_sales) {
            offerSales.push(
              ...offer.offer_sales.map((offerGroup) => {
                return {
                  ...offerGroup,
                  price_1: offer.price_1,
                  price_2: offer.price_2,
                  isGroup: true,
                  nameGroup: offer.name,
                  group_rules_exhibition: offer.group_rules_exhibition,
                  group_image_url: offer.group_image_url || offer.groupInfo?.image_url || offer.image_url,
                  groupInfo: {
                    _id: offer._id,
                    __typename: 'ProductsGroup',
                  },
                }
              })
            )
          } else if (offer.products) {
            offerSales.push(
              ...offer.products.map((offerGroup) => {
                return {
                  ...offerGroup,
                  price_1: offer.price_1,
                  price_2: offer.price_2,
                  isGroup: true,
                  nameGroup: offer.name,
                  group_rules_exhibition: offer.group_rules_exhibition,
                  group_image_url: offer.group_image_url || offer.groupInfo?.image_url || offer.image_url,
                  groupInfo: {
                    _id: offer._id,
                    __typename: 'ProductsGroup',
                  },
                }
              })
            )
          } else {
            offerSales.push(offer)
          }
        } else offerSales.push(offer)
      })
      return offerSales
    },
    handleDraftCampaign() {
      this.tryingToSaveAsDraft = true

      this.$v.$reset()
      this.$v.$touch()

      if (this.$v.$error) {
        return false
      }
    },
    isUndefined(value) {
      return typeof value === 'undefined'
    },
    async createDefaultCampaignVariableConfig() {
      try {
        if (!this.campaignVariablesConfiguration) {
          await this.getCampaignVariablesConfiguration({
            variableType: 'BEST_OFFER',
          })
        }

        const variables = {
          upsert_default: false,
          variable_name: '[melhor oferta]',
          variable_type: 'BEST_OFFER',
          channels: JSON.parse(JSON.stringify(this.campaignVariablesConfiguration)),
        }

        deepDelete(variables, '__typename')
        await this.upsertCampaignVariablesConfiguration(variables)
      } catch (error) {
        throw error
      }
    },
    formatBestOfferMessageText(message, messageChannel = null) {
      const regexBestOfferWithAmount = /\[(10|[1-9]) melhor oferta\]/i
      const hasBestOfferWithAmount = regexBestOfferWithAmount.test(message.text)

      if (hasBestOfferWithAmount) {
        const bestOfferVar = message.text.match(regexBestOfferWithAmount)
        message.amount_best_offers = bestOfferVar ? parseInt(bestOfferVar[1]) : null
        if (messageChannel && messageChannel.email_configuration_type === 'SIMPLE') {
          messageChannel.amount_best_offers = bestOfferVar ? parseInt(bestOfferVar[1]) : null
        }
        message.text = message.text.replace(regexBestOfferWithAmount, '[melhor oferta]')
      } else {
        const regexBestOfferWithoutAmount = /\[melhor oferta\]/i
        const hasBestOfferWithoutAmount = regexBestOfferWithoutAmount.test(message.text)

        if (hasBestOfferWithoutAmount && !message.amount_best_offers) {
          message.text = null
          this.$snackbar({
            message: 'Mensagem não pode conter variável de [melhor oferta] sem a quantidade de produtos definida.',
            snackbarColor: 'error',
          })
        }
      }

      return message
    },

    formatBestOfferMessageTitle(message) {
      const regexBestOfferWithAmount = /\[(10|[1-9]) melhor oferta\]/i
      const hasBestOfferWithAmount = regexBestOfferWithAmount.test(message.title)

      if (hasBestOfferWithAmount) {
        const bestOfferVar = message.title.match(regexBestOfferWithAmount)
        message.amount_best_offers_title = bestOfferVar ? parseInt(bestOfferVar[1]) : null
        message.title = message.title.replace(regexBestOfferWithAmount, '[melhor oferta]')
      } else {
        const regexBestOfferWithoutAmount = /\[melhor oferta\]/i
        const hasBestOfferWithoutAmount = regexBestOfferWithoutAmount.test(message.title)

        if (hasBestOfferWithoutAmount && !message.amount_best_offers_title) {
          message.title = null
          this.$snackbar({
            message: 'Mensagem não pode conter variável de [melhor oferta] sem a quantidade de produtos definida.',
            snackbarColor: 'error',
          })
        }
      }

      return message
    },

    formatBestOfferMessageInnerText(message) {
      const regexBestOfferWithAmount = /\[(10|[1-9]) melhor oferta\]/i
      const hasBestOfferWithAmount = regexBestOfferWithAmount.test(message.inner_message_text)

      if (hasBestOfferWithAmount) {
        const bestOfferVar = message.inner_message_text.match(regexBestOfferWithAmount)
        message.inner_amount_best_offers = bestOfferVar ? parseInt(bestOfferVar[1]) : null
        message.inner_message_text = message.inner_message_text.replace(regexBestOfferWithAmount, '[melhor oferta]')
      } else {
        const regexBestOfferWithoutAmount = /\[melhor oferta\]/i
        const hasBestOfferWithoutAmount = regexBestOfferWithoutAmount.test(message.inner_message_text)

        if (hasBestOfferWithoutAmount && !message.inner_amount_best_offers) {
          message.inner_message_text = null
          this.$snackbar({
            message: 'Mensagem não pode conter variável de [melhor oferta] sem a quantidade de produtos definida.',
            snackbarColor: 'error',
          })
        }
      }

      return message
    },

    formatBestOfferMessageInnerTitle(message) {
      const regexBestOfferWithAmount = /\[(10|[1-9]) melhor oferta\]/i
      const hasBestOfferWithAmount = regexBestOfferWithAmount.test(message.inner_message_title)

      if (hasBestOfferWithAmount) {
        const bestOfferVar = message.inner_message_title.match(regexBestOfferWithAmount)
        message.inner_amount_best_offers_title = bestOfferVar ? parseInt(bestOfferVar[1]) : null
        message.inner_message_title = message.inner_message_title.replace(regexBestOfferWithAmount, '[melhor oferta]')
      } else {
        const regexBestOfferWithoutAmount = /\[melhor oferta\]/i
        const hasBestOfferWithoutAmount = regexBestOfferWithoutAmount.test(message.inner_message_title)

        if (hasBestOfferWithoutAmount && !message.inner_amount_best_offers_title) {
          message.inner_message_title = null
          this.$snackbar({
            message: 'Mensagem não pode conter variável de [melhor oferta] sem a quantidade de produtos definida.',
            snackbarColor: 'error',
          })
        }
      }

      return message
    },

    formatBestOfferSubject(message, messageChannel = null) {
      const regexBestOfferWithAmount = /\[(10|[1-9]) melhor oferta\]/i
      const hasBestOfferWithAmount = regexBestOfferWithAmount.test(message.subject)

      if (hasBestOfferWithAmount) {
        const bestOfferVar = message.subject.match(regexBestOfferWithAmount)
        message.amount_best_offers_subject = bestOfferVar ? parseInt(bestOfferVar[1]) : null
        if (messageChannel && messageChannel.email_configuration_type === 'SIMPLE') {
          messageChannel.amount_best_offers_subject = bestOfferVar ? parseInt(bestOfferVar[1]) : null
        }
        message.subject = message.subject.replace(regexBestOfferWithAmount, '[melhor oferta]')
      } else {
        const regexBestOfferWithoutAmount = /\[melhor oferta\]/i
        const hasBestOfferWithoutAmount = regexBestOfferWithoutAmount.test(message.subject)

        if (hasBestOfferWithoutAmount && !message.amount_best_offers_subject) {
          message.subject = null
          this.$snackbar({
            message: 'Assunto não pode conter variável de [melhor oferta] sem a quantidade de produtos definida.',
            snackbarColor: 'error',
          })
        }
      }

      return message
    },
    scheduleSettingsFormatted() {
      return this.campaign.messages_dispatcher?.settings.schedule_settings?.map((settings) => {
        const isUserSelector = settings.selector_channels_type === 'USER'
        const model = this.campaign.messages_dispatcher.settings.model

        const baseSchedule = {
          id: settings.id,
          active: settings.active,
          initial_hour: settings.initial_hour,
          final_hour: settings.final_hour,
          selector_channels_type: settings.selector_channels_type,
        }

        if (model === 'MARIA') {
          baseSchedule.week_day = null
          baseSchedule.send_date = null
        }

        if (model === 'RECURRENT') {
          baseSchedule.week_day = this.formattedWeekDays[settings.week_day]
          baseSchedule.send_date = null
        }

        if (model === 'STATIC_DATE') {
          baseSchedule.send_date = DateTime.fromFormat(settings.send_date, 'dd/MM/yyyy').toFormat('yyyy-MM-dd')
          baseSchedule.week_day = null
        }

        const buildChannel = (channelName) => {
          if (!settings.channels[channelName]) {
            return {}
          }

          const channel = JSON.parse(JSON.stringify(settings.channels[channelName]))
          if (channelName === 'email') {
            if (!channel?.message?.email_configuration_type) {
              return {}
            }
            if (channel?.message?.email_configuration_type === 'BUILDER') {
              if (!Object.keys(channel?.message?.builder_email_configuration).length > 0) {
                return {}
              }
            }
            if (channel?.message?.email_configuration_type === 'SIMPLE') {
              if (!channel?.message?.simple_email_configuration?.text) {
                return {}
              }
            }
            if (channel?.message?.email_configuration_type === 'HTML') {
              if (!channel?.message?.html_email_configuration?.text) {
                return {}
              }
            }
          } else if (!channel?.message?.text && !channel?.message?.title) {
            return {}
          }

          delete channel.name
          delete channel.icon

          if (channelName === 'whatsapp') {
            channel.senders = channel.senders.map((whatsapp) => whatsapp.number)
          }

          if (channelName === 'email') {
            if (channel.message?.simple_email_configuration?.text) {
              channel.message.simple_email_configuration = {
                ...channel.message.simple_email_configuration,
                ...this.formatBestOfferMessageText(channel.message.simple_email_configuration, channel.message),
              }
            }
            if (channel.message?.simple_email_configuration?.subject) {
              channel.message.simple_email_configuration = {
                ...channel.message.simple_email_configuration,
                ...this.formatBestOfferSubject(channel.message.simple_email_configuration, channel.message),
              }
            }
          } else if (channelName === 'push') {
            channel.message = {
              ...channel.message,
              ...this.formatBestOfferMessageText(channel.message),
            }
            channel.message = {
              ...channel.message,
              ...this.formatBestOfferMessageTitle(channel.message),
            }
            channel.message = {
              ...channel.message,
              ...this.formatBestOfferMessageInnerText(channel.message),
            }
            channel.message = {
              ...channel.message,
              ...this.formatBestOfferMessageInnerTitle(channel.message),
            }
          } else if (channel.message?.text) {
            channel.message = { ...channel.message, ...this.formatBestOfferMessageText(channel.message) }
          }

          return {
            [channelName]: {
              ...(isUserSelector ? { priority: channel.priority } : {}),
              active: channel.active,
              ...channel,
            },
          }
        }

        const channels = {
          ...buildChannel('sms'),
          ...buildChannel('whatsapp'),
          ...buildChannel('push'),
          ...buildChannel('email'),
        }

        return {
          ...baseSchedule,
          channels,
        }
      })
    },
    offerSalesFormatted() {
      let offer_sales = []
      if (this.campaign.offer_sales?.length > 0) {
        this.campaign.offer_sales.map((offer) => {
          if (offer?.offer_sales?.length > 0) {
            offer?.offer_sales?.map((product) => {
              offer_sales.push({
                sku: product.sku,
                eans: product.eans,
                name: product.name,
                image_url: product.image,
                ...this.formattedBaseOfferSales(product),
                ...this.formattedBaseOfferGroup(offer),
                active: this.isUndefined(product.active) ? true : product.active,
              })
            })
          } else {
            offer_sales.push({
              sku: offer.sku,
              eans: offer.eans,
              name: offer.name,
              image_url: offer.image,
              ...this.formattedBaseOfferSales(offer),
              ...(offer.isGroup
                ? {
                    ...this.formattedBaseOfferGroup(offer),
                  }
                : {}),
            })
          }
        })
      }
      return offer_sales
    },
    getFormatedStartDate(start_date) {
      if (!start_date) return null
      const hasTime = start_date.includes(':')
      return hasTime ? start_date : `${start_date} 00:00:00`
    },
    getFormatedEndDate(end_date) {
      if (!end_date) return null
      const hasTime = end_date.includes(':')
      return hasTime ? end_date : `${end_date} 23:59:59`
    },
    offerSchedulesFormatted() {
      let offer_schedules

      const formattedBaseOffer = (offer) => {
        return {
          sku: offer.sku,
          name: offer.name,
          eans: offer.eans || offer?.product?.eans,
          image_url: offer.image_url || offer?.product?.image,
          measure_name: offer?.measure_name || offer?.product?.measure_name,
        }
      }

      const formattedBaseOfferCombo = (offer) => {
        return {
          sku: offer.sku,
          name: offer.name,
          eans: offer.eans || offer?.product?.eans,
        }
      }

      const formattedBaseOfferPercentageSales = (offer) => {
        return {
          image_url: offer.image_url || offer?.product?.image,
          active: this.isUndefined(offer.active) ? true : offer.active,
          maximum_per_client: this.isUndefined(offer.maximum_per_client) ? 0 : offer.maximum_per_client,
          minimum_per_client: this.isUndefined(offer.minimum_per_client) ? 0 : offer.minimum_per_client,
          percentage_discount: this.isUndefined(offer.percentage_discount) ? 0 : offer.percentage_discount,
          measure_name: offer?.measure_name || offer?.product?.measure_name,
        }
      }

      const formattedBaseOfferPointsSales = (offer) => {
        return {
          image_url: offer.image_url || offer?.product?.image,
          active: this.isUndefined(offer.active) ? true : offer.active,
          maximum_per_client: this.isUndefined(offer.maximum_per_client) ? 0 : offer.maximum_per_client,
          minimum_per_client: this.isUndefined(offer.minimum_per_client) ? 0 : offer.minimum_per_client,
          price: this.isUndefined(offer.price) ? 0 : offer.price,
          additional_points_rate: offer.additional_points_rate,
          measure_name: offer?.measure_name || offer?.product?.measure_name,
        }
      }

      const formattedBaseOfferTakepaySales = (offer) => {
        return {
          image_url: offer.image_url || offer?.product?.image,
          active: this.isUndefined(offer.active) ? true : offer.active,
          maximum_per_client: this.isUndefined(offer.maximum_per_client) ? 0 : offer.maximum_per_client,
          take: this.isUndefined(offer.take) ? 0 : offer.take,
          pay: this.isUndefined(offer.pay) ? 0 : offer.pay,
          measure_name: offer?.measure_name || offer?.product?.measure_name,
        }
      }

      const formattedBaseOfferExpirationCashback = (offer) => {
        return {
          ...offer.expiration_cashback,
          expires_at: offer.expiration_cashback?.type === 'BY_DATE' ? `${offer.expiration_cashback?.expires_at} 23:59:59` : null,
        }
      }

      const formattedBaseOfferCashbackPercentage = (offer) => {
        return {
          image_url: offer.image_url || offer?.product?.image,
          active: this.isUndefined(offer.active) ? true : offer.active,
          maximum_per_client: this.isUndefined(offer.maximum_per_client) ? 0 : offer.maximum_per_client,
          minimum_per_client: this.isUndefined(offer.minimum_per_client) ? 0 : offer.minimum_per_client,
          price_1: this.isUndefined(offer.price_1) ? 0 : offer.price_1,
          cashback_percentage: offer.cashback_percentage,
          expiration_cashback: formattedBaseOfferExpirationCashback(offer),
          measure_name: offer?.measure_name || offer?.product?.measure_name,
        }
      }

      const formattedBaseOfferCashbackAbsolute = (offer) => {
        return {
          image_url: offer.image_url || offer?.product?.image,
          active: this.isUndefined(offer.active) ? true : offer.active,
          maximum_per_client: this.isUndefined(offer.maximum_per_client) ? 0 : offer.maximum_per_client,
          minimum_per_client: this.isUndefined(offer.minimum_per_client) ? 0 : offer.minimum_per_client,
          price: this.isUndefined(offer.price) ? 0 : offer.price,
          cashback_absolute: offer.cashback_absolute,
          expiration_cashback: formattedBaseOfferExpirationCashback(offer),
          measure_name: offer?.measure_name || offer?.product?.measure_name,
        }
      }

      if (this.campaign.offer_schedules?.length > 0) {
        offer_schedules = this.campaign.offer_schedules.map((offer) => {
          return {
            offer_schedule_id: offer.offer_schedule_id || offer._id || offer?.offer_schedule?._id,
            name: offer.name,
            image_url: offer.image_url || offer?.product?.image,
            active: this.isUndefined(offer.active) ? true : offer.active,
            draft: offer?.draft ?? offer?.offer_schedule?.draft,
            from_franchise: offer.from_franchise,
            auto_distributed: offer?.auto_distributed ?? offer?.offer_schedule?.auto_distributed ?? false,
            start_date: this.getFormatedStartDate(offer.start_date),
            end_date: this.getFormatedEndDate(offer.end_date),
            units: offer.units.map((unit) => unit.id_erp || unit),
            offer_sales: Array.from(
              new Map(
                offer.offer_sales
                  .map((offer) => {
                    if (offer?.offer_sales?.length) {
                      return offer.offer_sales.map((sales) => {
                        return {
                          ...this.formattedBaseOfferSales(offer),
                          ...this.formattedBaseOfferGroup(offer),
                          ...formattedBaseOffer(sales),
                        }
                      })
                    }
                    return {
                      ...this.formattedBaseOfferSales(offer),
                      ...(this.hasOfferGroupAssociation(offer)
                        ? {
                            ...this.formattedBaseOfferGroup(offer),
                          }
                        : {}),
                      ...formattedBaseOffer(offer),
                    }
                  })
                  .flat()
                  .map((item) => [item.sku, item])
              ).values()
            ),
            percentage_offer_sales: Array.from(
              new Map(
                offer.percentage_offer_sales
                  .map((offer) => {
                    if (offer?.percentage_offer_sales?.length) {
                      return offer.percentage_offer_sales.map((sales) => {
                        return {
                          ...formattedBaseOfferPercentageSales(offer),
                          ...this.formattedBaseOfferGroup(offer),
                          ...formattedBaseOffer(sales),
                        }
                      })
                    }
                    return {
                      ...formattedBaseOfferPercentageSales(offer),
                      ...(this.hasOfferGroupAssociation(offer)
                        ? {
                            ...this.formattedBaseOfferGroup(offer),
                          }
                        : {}),
                      ...formattedBaseOffer(offer),
                    }
                  })
                  .flat()
                  .map((item) => [item.sku, item])
              ).values()
            ),
            points_offer_sales: Array.from(
              new Map(
                offer.points_offer_sales
                  .map((offer) => {
                    if (offer?.points_offer_sales?.length) {
                      return offer.points_offer_sales.map((sales) => {
                        return {
                          ...formattedBaseOfferPointsSales(offer),
                          ...this.formattedBaseOfferGroup(offer),
                          ...formattedBaseOffer(sales),
                        }
                      })
                    }
                    return {
                      ...formattedBaseOfferPointsSales(offer),
                      ...(this.hasOfferGroupAssociation(offer)
                        ? {
                            ...this.formattedBaseOfferGroup(offer),
                          }
                        : {}),
                      ...formattedBaseOffer(offer),
                    }
                  })
                  .flat()
                  .map((item) => [item.sku, item])
              ).values()
            ),
            takepay_offer_sales: Array.from(
              new Map(
                offer.takepay_offer_sales
                  .map((offer) => {
                    if (offer?.takepay_offer_sales?.length) {
                      return offer.takepay_offer_sales.map((takepay) => {
                        return {
                          ...formattedBaseOfferTakepaySales(offer),
                          ...this.formattedBaseOfferGroup(offer),
                          ...formattedBaseOffer(takepay),
                        }
                      })
                    }
                    return {
                      ...formattedBaseOfferTakepaySales(offer),
                      ...(this.hasOfferGroupAssociation(offer)
                        ? {
                            ...this.formattedBaseOfferGroup(offer),
                          }
                        : {}),
                      ...formattedBaseOffer(offer),
                    }
                  })
                  .flat()
                  .map((item) => [item.sku, item])
              ).values()
            ),
            combo_offer_sales: offer.combo_offer_sales.map((offer) => {
              return {
                name: offer.name,
                maximum_per_client: offer.maximum_per_client,
                image: offer.image,
                combo_id: offer.combo_id,
                active: offer.active,
                trigger_products: offer.trigger_products
                  .map((trigger) => {
                    if (trigger.trigger_products?.length) {
                      return trigger.trigger_products.map((product) => {
                        return {
                          quantity: trigger.quantity,
                          group_name: trigger.group_name,
                          ...this.getOfferGroupReferenceFields(trigger),
                          ...formattedBaseOfferCombo(product),
                        }
                      })
                    }
                    return {
                      quantity: trigger.quantity,
                      group_name: trigger.group_name,
                      ...this.getOfferGroupReferenceFields(trigger),
                      ...formattedBaseOfferCombo(trigger),
                    }
                  })
                  .flat(),
                target_products: offer.target_products
                  .map((target) => {
                    if (target.target_products?.length) {
                      return target.target_products.map((product) => {
                        return {
                          percentage_discount: target.percentage_discount,
                          group_name: target.group_name,
                          ...this.getOfferGroupReferenceFields(target),
                          ...formattedBaseOfferCombo(product),
                        }
                      })
                    }
                    return {
                      percentage_discount: target.percentage_discount,
                      group_name: target.group_name,
                      ...this.getOfferGroupReferenceFields(target),
                      ...formattedBaseOfferCombo(target),
                    }
                  })
                  .flat(),
              }
            }),
            cashback_percentage_offer_sales: Array.from(
              new Map(
                offer.cashback_percentage_offer_sales
                  .map((offer) => {
                    if (offer?.cashback_percentage_offer_sales?.length) {
                      return offer.cashback_percentage_offer_sales.map((sales) => {
                        return {
                          ...formattedBaseOfferCashbackPercentage(offer),
                          ...this.formattedBaseOfferGroup(offer),
                          ...formattedBaseOffer(sales),
                        }
                      })
                    }
                    return {
                      ...formattedBaseOfferCashbackPercentage(offer),
                      ...(this.hasOfferGroupAssociation(offer)
                        ? {
                            ...this.formattedBaseOfferGroup(offer),
                          }
                        : {}),
                      ...formattedBaseOffer(offer),
                    }
                  })
                  .flat()
                  .map((item) => [item.sku, item])
              ).values()
            ),
            cashback_absolute_offer_sales: Array.from(
              new Map(
                offer.cashback_absolute_offer_sales
                  .map((offer) => {
                    if (offer?.cashback_absolute_offer_sales?.length) {
                      return offer.cashback_absolute_offer_sales.map((sales) => {
                        return {
                          ...formattedBaseOfferCashbackAbsolute(offer),
                          ...(this.hasOfferGroupAssociation(offer) || offer.group_rules_exhibition
                            ? {
                                ...this.formattedBaseOfferGroup(offer),
                              }
                            : {}),
                          ...formattedBaseOffer(sales),
                        }
                      })
                    }
                    return {
                      ...formattedBaseOfferCashbackAbsolute(offer),
                      ...(this.hasOfferGroupAssociation(offer)
                        ? {
                            ...this.formattedBaseOfferGroup(offer),
                          }
                        : {}),
                      ...formattedBaseOffer(offer),
                    }
                  })
                  .flat()
                  .map((item) => [item.sku, item])
              ).values()
            ),
          }
        })
      }

      return offer_schedules
    },
    validateAndReplaceDefaultTexts(html, isAutoDistributed = false) {
      if (typeof html !== 'string' || !html) return html

      let processedHtml = html

      const BODY_RE = /(<body\b[^>]*>)([\s\S]*?)(<\/body>)/i
      const m = processedHtml.match(BODY_RE)
      if (!m) return { html: processedHtml }

      const bodyOpen = m[1]
      let bodyInner = m[2]
      const bodyClose = m[3]

      const VAR_RE = /(\{\{[^{}]+\}\}|\{[^{}]+\})/g
      const varPlaceholders = []
      bodyInner = bodyInner.replace(VAR_RE, (v) => {
        const key = `__VAR_PLACEHOLDER_${varPlaceholders.length}__`
        varPlaceholders.push(v)
        return key
      })

      const offerCardMappings = {
        Unidade: '{unidade}',
        '[data validade]': '{fim da validade}',
        Desconto: '{desconto da oferta}',
        'Imagem da oferta': '{imagem da oferta}',
        'Regra min/máx': '{regra min/max}',
        'Mecânica oferta': '{mecanica oferta}',
      }

      for (let i = 1; i <= 10; i++) {
        offerCardMappings[`${i}ª Melhor Oferta`] = '{nome da oferta}'
        if (isAutoDistributed) {
          offerCardMappings[`${i}ª Melhor Oferta – Recompra`] = '{nome da oferta}'
          offerCardMappings[`${i}ª Melhor Oferta – Ativação`] = '{nome da oferta}'
          offerCardMappings[`${i}ª Melhor Oferta – Resgate`] = '{nome da oferta}'
        }
      }
      offerCardMappings['Melhor Oferta'] = '{nome da oferta}'

      Object.entries(offerCardMappings).forEach(([defaultText, variable]) => {
        const escaped = defaultText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const regex = new RegExp(`(?<![\\p{L}\\p{N}_\\{])${escaped}(?![\\p{L}\\p{N}_\\}])`, 'gu')
        bodyInner = bodyInner.replace(regex, variable)
      })

      if (isAutoDistributed) {
        const productNameRegex =
          /(<div[^>]*class="[^"]*(?:product-name|offer-card-product-name)[^"]*"[^>]*>)([^<]*?)(\s*[–—-]\s*(?:Recompra|Ativação|Resgate))([^<]*?)(<\/div>)/gi
        bodyInner = bodyInner.replace(productNameRegex, (match, openTag, beforeText, modelText, afterText, closeTag) => {
          return openTag + beforeText.trim() + closeTag
        })
      }

      const offerCardImageContainerRegex = /<table[^>]*class="[^"]*offer-card-image-container[^"]*"[^>]*>[\s\S]*?<\/table>/gi
      bodyInner = bodyInner.replace(
        offerCardImageContainerRegex,
        `<table class="offer-card-image-container" border="0" cellpadding="0" cellspacing="0" style="background-image: url('{imagem da oferta}'); background-size: cover; background-position: center; background-repeat: no-repeat; border-radius: 8px; width: 173px; height: 173px; text-align: center; background-size: cover !important; margin: 0 auto; border-collapse: separate; border-spacing: 0; overflow: hidden;"></table>`
      )

      const offerCardSmallImageCellRegex = /<table[^>]*class="[^"]*offer-card-small-image-table[^"]*"[^>]*>[\s\S]*?<\/table>/gi
      bodyInner = bodyInner.replace(
        offerCardSmallImageCellRegex,
        `<table class="offer-card-small-image-table" style="background-image: url('{imagem da oferta}'); background-size: cover; background-position: center; background-repeat: no-repeat; width: 112px; height: 112px; margin: 0 auto !important; border-radius: 8px; display: block;table-layout: fixed;" role="presentation" cellpadding="0" cellspacing="0" border="0"></table>`
      )

      bodyInner = bodyInner.replace(/__VAR_PLACEHOLDER_(\d+)__/g, (_, i) => varPlaceholders[Number(i)])

      processedHtml = processedHtml.replace(BODY_RE, `${bodyOpen}${bodyInner}${bodyClose}`)
      return { html: processedHtml }
    },
    replaceHtmlBestOfferWithAmount(html) {
      const regexBestOfferWithAmount = /\[(\d+) melhor oferta\]/gi
      const hasBestOfferWithAmount = regexBestOfferWithAmount.test(html)

      if (hasBestOfferWithAmount) {
        const allMatches = [...html.matchAll(regexBestOfferWithAmount)]
        const amountBestOffers = allMatches.length

        const processedHtml = html.replace(regexBestOfferWithAmount, '[melhor oferta]')

        return { html: processedHtml, amount_best_offers: amountBestOffers }
      }

      return { html: html }
    },
    preparePayloadWithValidation(data) {
      let payload = { ...data }

      const hasAutoDistributedOfferSchedule = (this.campaign?.offer_schedules || payload?.offer_schedules || []).some((offerSchedule) => {
        return offerSchedule?.auto_distributed === true || offerSchedule?.offer_schedule?.auto_distributed === true
      })

      if (payload?.messages_dispatcher?.settings?.schedule_settings) {
        payload.messages_dispatcher.settings.schedule_settings.forEach((schedule) => {
          if (schedule.channels?.email?.message?.email_configuration_type === 'BUILDER') {
            const html = schedule.channels.email.message.processed_html
            if (html) {
              const validationResult = this.validateAndReplaceDefaultTexts(html, hasAutoDistributedOfferSchedule)
              schedule.channels.email.message.processed_html = validationResult.html
            }
          } else if (schedule.channels?.email?.message?.email_configuration_type === 'SIMPLE') {
            const html = schedule.channels.email.message.processed_html
            if (html) {
              const validationResult = this.replaceHtmlBestOfferWithAmount(html)
              schedule.channels.email.message.processed_html = validationResult.html
            }
          }
        })
      }
      return payload
    },
    async upsertDraftMariaCampaign(isDraft, stepToSave) {
      this.loadingSaveDraft = true

      let client_targets_distribution = {
        actives: this.campaign.client_targets_distribution?.actives / 100,
        at_risk: this.campaign.client_targets_distribution?.at_risk / 100,
        losses: this.campaign.client_targets_distribution?.losses / 100,
        never_bought: this.campaign.client_targets_distribution?.never_bought / 100,
        recent_inactives: this.campaign.client_targets_distribution?.recent_inactives / 100,
      }

      if (!isDraft || stepToSave >= 4) {
        try {
          if (this.campaign?.messages_dispatcher?.settings?.campaign_variable_configurations?.length === 0) {
            await this.createDefaultCampaignVariableConfig()
          }
        } catch (error) {
          this.$snackbar({ message: 'Erro ao criar configuração de variáveis padrão.', snackbarColor: 'error' })
          console.error(error)
          return
        }
      }

      let data = {
        _id: this.campaign._id || '',
        title: this.campaign.title,
        description: this.campaign.description,
        initial_date: `${this.campaign.initial_date} 00:00:00`,
        final_date: `${this.campaign.final_date} 23:59:59`,
        units: this.campaign.units?.map((unit) => unit.value || unit.id_erp || unit),
        messages_dispatcher: {
          budget: {
            limiter: this.campaign.messages_dispatcher?.budget?.limiter,
            channels: this.campaign.messages_dispatcher?.budget?.channels,
          },
          settings: {
            model: this.campaign.messages_dispatcher?.settings?.model,
            limit_per_consumer:
              this.campaign.messages_dispatcher?.settings?.model === 'MARIA' ? this.campaign.messages_dispatcher?.settings?.limit_per_consumer : null,
            product_variable_type: this.campaign.messages_dispatcher?.settings?.product_variable_type,
            campaign_variable_configurations: [this.campaignVariableConfigurationId],
            schedule_settings: this.scheduleSettingsFormatted(),
          },
        },
        segments: [...(this.campaign.segments?.map((segment) => segment._id) || null)],
        offer_sales: this.offerSalesFormatted(),
        offer_schedules: this.offerSchedulesFormatted(),
        client_targets_distribution,
        product_targets: { repurchase: 1, upsell: 0 },
      }
      deepDelete(data, ['__typename'])

      const createPayload = (step) => {
        const basePayload = {
          _id: data._id,
          title: data.title,
          description: data.description,
          offer_sales: data.offer_sales,
          offer_schedules: data.offer_schedules,
        }

        switch (step) {
          case 1:
          case 2:
            return basePayload
          case 3:
            return {
              ...basePayload,
              segments: data.segments,
              client_targets_distribution: data.client_targets_distribution,
            }
          case 4:
            return {
              ...basePayload,
              segments: data.segments,
              client_targets_distribution: data.client_targets_distribution,
              messages_dispatcher: data.messages_dispatcher,
              initial_date: data.initial_date,
              final_date: data.final_date,
              units: data.units,
            }
          default:
            return { ...data }
        }
      }

      const payload = isDraft ? createPayload(stepToSave) : data

      const validatedPayload = this.preparePayloadWithValidation(payload)

      await this.$apollo
        .mutate({
          mutation: isDraft ? QUERY_UPSERT_DRAFT_OMNI_CAMPAIGN : QUERY_UPSERT_OMNI_CAMPAIGN,
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
          variables: {
            data: validatedPayload,
          },
        })
        .then(() => {
          Swal.fire({
            text: 'Campanha salva',
            icon: 'success',
            showConfirmButton: false,
            timer: 1000,
            customClass: {
              content: 'content-class',
            },
          })
          this.forcePush = true
          this.$store.commit('RESET_MARIA_CAMPAIGN_MODULE')
          this.$router.push({ name: 'maria-campaign' })
        })
        .catch((error) => {
          let text = 'Erro ao salvar campanha'
          if (error.message === 'GraphQL error: Initial date and final date must be after the current date')
            text = 'Data inicial e data final da campanha deve ser igual ou maior à data atual.'

          Swal.fire({
            text,
            icon: 'error',
            showConfirmButton: false,
            timer: 1500,
            customClass: {
              content: 'content-class',
            },
          })
        })
      this.loadingSaveDraft = false
    },
  },
}
